<template>
  <hb-basic-page
    :title="$t('inventory.title')"
    fluid
    :loading="loading"
    :extension-height="48"
    class="inventory-view"
    :no-container-padding="$vuetify.breakpoint.xsOnly"
    :no-gutters="$vuetify.breakpoint.xsOnly"
    v-if="$isTenantAdmin"
  >
    <template v-slot:app-bar-extension>
      <v-tabs v-model="selectedAction" background-color="grey darken-3" :grow="$vuetify.breakpoint.xsOnly">
        <v-tab v-for="tab in tabs" :key="tab.value" :href="'#' + tab.value">
          <v-icon v-if="$vuetify.breakpoint.smAndUp" left>{{ tab.icon }}</v-icon>
          {{ tab.text }}
        </v-tab>
      </v-tabs>
    </template>

    <v-container fluid>
      <v-row v-if="showWarehouseSelect" dense align="center" class="mb-2">
        <v-col cols="12" sm="6" md="3">
          <v-select
            v-model="warehouseId"
            :items="warehouses"
            item-text="name"
            item-value="id"
            :label="$t('inventory.warehouse')"
            :disabled="hasPendingRows"
            :title="hasPendingRows ? $t('inventory.warehouse_locked_hint') : null"
            hide-details
            prepend-inner-icon="mdi-warehouse"
            outlined
            dense
          />
        </v-col>
        <v-col v-if="$isTenantAdmin" cols="auto">
          <v-btn text color="primary" @click="$refs.warehouseManagement.open()">
            <v-icon left>mdi-cog</v-icon>
            {{ $t('inventory.manage_warehouses') }}
          </v-btn>
        </v-col>
      </v-row>

      <!-- Ei yhtään varastoa: saldot ja kirjaukset edellyttävät varastoa -->
      <no-content-view
        v-if="!loading && !hasWarehouses && selectedAction !== 'purchase_invoices'"
        :title="$t('inventory.no_warehouses_title')"
        :description="$t($isTenantAdmin ? 'inventory.no_warehouses_admin' : 'inventory.no_warehouses_help')"
        title-icon="mdi-warehouse"
        :reload-button="false"
        :function-button="$isTenantAdmin"
        function-button-icon="mdi-plus"
        :function-button-text="$t('inventory.create_warehouse')"
        :prop-function="() => $refs.warehouseManagement.open()"
      />

      <!-- Warehouse Balances View -->
      <v-card v-if="selectedAction === 'balances' && hasWarehouses" class="mb-4" :elevation="$vuetify.breakpoint.xsOnly ? 0 : ''">
        <v-card-title class="subtitle-1 text-uppercase font-weight-medium">
          <v-icon left>mdi-warehouse</v-icon>
          {{ $t('inventory.warehouse_balances') }}
        </v-card-title>
        <v-card-text class="pb-0">
          <v-row dense align="center">
            <v-col cols="12" sm="6" md="4">
              <v-text-field
                v-model="searchBalance"
                prepend-inner-icon="mdi-magnify"
                :label="$t('common.search')"
                single-line
                hide-details
                dense
                outlined
                clearable
              />
            </v-col>
            <v-spacer />
            <v-col cols="12" sm="auto">
              <v-btn color="primary" outlined :block="$vuetify.breakpoint.xsOnly" @click="openSnapshotDialog">
                <v-icon left>mdi-file-excel</v-icon>
                {{ $t('inventory.download_snapshot') }}
              </v-btn>
              <v-btn
                v-if="$isTenantAdmin"
                :icon="$vuetify.breakpoint.smAndUp"
                :text="$vuetify.breakpoint.xsOnly"
                :block="$vuetify.breakpoint.xsOnly"
                color="primary"
                :class="$vuetify.breakpoint.xsOnly ? 'mt-2' : 'ml-1'"
                :aria-label="$t('inventory.recalculate_balances')"
                :title="$t('inventory.recalculate_balances')"
                @click="recalculateDialog = true"
              >
                <v-icon :left="$vuetify.breakpoint.xsOnly">mdi-refresh</v-icon>
                <span v-if="$vuetify.breakpoint.xsOnly">{{ $t('inventory.recalculate_balances') }}</span>
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
        <v-data-table
          :headers="balanceHeaders"
          :items="filteredBalances"
          :loading="loadingBalances"
          :items-per-page="50"
          :footer-props="{ itemsPerPageOptions: [50, 100, 500, -1] }"
        >
          <template v-slot:item.item.typeName="{ item }">
            {{ item.item.typeName }}
          </template>
          <template v-slot:item.quantity="{ item }">
            <span :class="item.quantity <= 0 ? 'error--text' : ''">
              <v-icon
                v-if="item.quantity <= 0"
                small
                color="error"
                class="no-gradient"
                :aria-label="$t('inventory.negative_balance')"
                :title="$t('inventory.negative_balance')"
              >mdi-alert-circle-outline</v-icon>
              {{ formatQuantity(item.quantity) }} {{ unitLabel(item.item) }}
            </span>
          </template>
          <template v-slot:no-data>
            <no-content-view
              :title="$t('inventory.no_balances_title')"
              :description="$t('inventory.no_balances_help', { warehouse: selectedWarehouseName })"
              title-icon="mdi-package-variant"
              :reload-button="false"
            />
          </template>
          <template v-slot:item.updatedAt="{ item }">
            {{ $formatDateNoTime(item.updatedAt) }}
          </template>
          <template v-slot:item.actions="{ item }">
            <v-btn v-if="$vuetify.breakpoint.xsOnly" text small color="primary" @click="showItemHistory(item.item.id)">
              <v-icon small left>mdi-history</v-icon>
              {{ $t('inventory.show_item_history') }}
            </v-btn>
            <v-tooltip v-else bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon small v-bind="attrs" v-on="on" :aria-label="$t('inventory.show_item_history')" @click="showItemHistory(item.item.id)">
                  <v-icon small>mdi-history</v-icon>
                </v-btn>
              </template>
              <span>{{ $t('inventory.show_item_history') }}</span>
            </v-tooltip>
          </template>
        </v-data-table>
      </v-card>

      <inventory-transaction-form
        v-if="selectedAction === 'transactions' && hasWarehouses"
        v-model="transactionType"
        :warehouse-id="warehouseId"
        :warehouses="warehouses"
        :items="trackableItems"
        @saved="onTransactionSaved"
        @pending="formPending = $event"
      />

      <!-- Vahvistamattomat lähetteet -->
      <v-card
        v-if="selectedAction === 'transactions' && hasWarehouses && (draftDispatchNotes.length > 0 || loadingDraftDispatchNotes)"
        class="mb-4"
        :elevation="$vuetify.breakpoint.xsOnly ? 0 : ''"
      >
        <v-card-title class="subtitle-1 text-uppercase font-weight-medium">
          <v-icon left>mdi-clipboard-text-clock-outline</v-icon>
          {{ $t('inventory.pending_dispatch_notes') }}
          <v-chip small class="ml-2" color="warning" dark>{{ draftDispatchNotes.length }}</v-chip>
        </v-card-title>
        <v-data-table
          :headers="draftDispatchHeaders"
          :items="draftDispatchNotes"
          :loading="loadingDraftDispatchNotes"
          :items-per-page="10"
          dense
          style="cursor: pointer"
          @click:row="openDispatchNoteSite"
        >
          <template v-slot:item.siteName="{ item }">
            <router-link
              v-if="item.siteId"
              :to="{ name: 'tenant_site_edit_site', params: { id: item.siteId, tab: 'materials' } }"
            >{{ item.siteName }}</router-link>
          </template>
          <template v-slot:item.direction="{ item }">
            <v-chip small :color="item.direction === 'TO_SITE' ? 'success' : 'warning'" dark>
              {{ $t('inventory.directions.' + item.direction) }}
            </v-chip>
          </template>
          <template v-slot:item.createdAt="{ item }">{{ formatDate(item.createdAt) }}</template>
        </v-data-table>
      </v-card>

      <!-- Transaction History -->
      <v-card v-if="selectedAction === 'transactions' && hasWarehouses" class="mb-4" :elevation="$vuetify.breakpoint.xsOnly ? 0 : ''">
        <v-card-title class="subtitle-1 text-uppercase font-weight-medium">
          <v-icon left>mdi-history</v-icon>
          {{ $t('inventory.transaction_history') }}
        </v-card-title>
        <v-card-text class="pb-0">
          <v-row dense>
            <v-col cols="12" sm="6" md="3">
              <v-text-field
                :value="listState.q"
                :label="$t('inventory.filter_by_description')"
                prepend-inner-icon="mdi-magnify"
                clearable
                outlined
                dense
                hide-details
                @input="debounceSearch"
              />
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <item-combobox
                :value="historyItemObject"
                :items="trackableItems"
                :label="$t('inventory.filter_by_item')"
                hide-details
                @change="v => (historyItem = v ? v.id : null)"
              />
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-menu v-model="historyDateMenu" :close-on-content-click="false" offset-y min-width="auto">
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    :value="formatIsoDateRangeFi([listState.from, listState.to])"
                    :label="$t('inventory.history_period')"
                    prepend-inner-icon="mdi-calendar"
                    readonly
                    clearable
                    outlined
                    dense
                    hide-details
                    v-bind="attrs"
                    v-on="on"
                    @click:clear="updateQuery({ from: '', to: '' })"
                  />
                </template>
                <v-date-picker v-model="historyDates" range no-title locale="fi" :first-day-of-week="1" @input="onHistoryDates" />
              </v-menu>
            </v-col>
            <v-col cols="12" sm="6" md="3" class="d-flex justify-end">
              <v-btn color="primary" outlined :block="$vuetify.breakpoint.xsOnly" @click="historyExcelDialog = true">
                <v-icon left>mdi-file-excel</v-icon>
                {{ $t('inventory.download_history_excel') }}
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
        <v-data-table
          :headers="transactionHeadersWithActions"
          :items="transactions.content || []"
          :options="tableOptions"
          @update:options="onTableOptions"
          :server-items-length="transactions.totalElements || 0"
          :loading="loadingTransactions"
          :footer-props="{ itemsPerPageOptions: [50, 100, 500, -1] }"
        >
          <template v-slot:item.item.name="{ item }">
            <span>{{ item.item && item.item.name }}</span>
            <div v-if="item.item && item.item.typeName" class="caption text--secondary">
              {{ item.item.typeName }}
            </div>
          </template>
          <template v-slot:item.transactionType="{ item }">
            <v-chip :color="getTransactionTypeColor(item.transactionType)" small dark>
              {{ $t('inventory.transaction_types.' + item.transactionType) }}
            </v-chip>
          </template>
          <template v-slot:item.quantity="{ item }">
            <span :class="item.quantity >= 0 ? 'success--text' : 'error--text'">
              {{ item.quantity >= 0 ? '+' : '' }}{{ formatQuantity(item.quantity) }}
            </span>
          </template>
          <template v-slot:item.siteName="{ item }">
            <router-link
              v-if="item.siteId"
              :to="{ name: 'tenant_site_edit_site', params: { id: item.siteId, tab: 'materials' } }"
            >{{ item.siteName }}</router-link>
          </template>
          <template v-slot:item.warehouseName="{ item }">{{ warehouseLabel(item) }}</template>
          <template v-slot:item.createdAt="{ item }">
            {{ formatDate(item.createdAt) }}
          </template>
          <template v-slot:item.actions="{ item }">
            <span class="text-no-wrap">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon small v-bind="attrs" v-on="on" :aria-label="$t('inventory.edit_transaction')" @click="openEditTransaction(item)">
                    <v-icon small>mdi-pencil</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t('inventory.edit_transaction') }}</span>
              </v-tooltip>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon small v-bind="attrs" v-on="on" :aria-label="$t('inventory.delete_transaction')" @click="openDeleteTransaction(item)">
                    <v-icon small>mdi-delete</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t('inventory.delete_transaction') }}</span>
              </v-tooltip>
            </span>
          </template>
        </v-data-table>
      </v-card>

      <!-- Ostolaskut (Netvisor, kohdelaskenta = Varasto) -->
      <inventory-purchase-invoices v-if="selectedAction === 'purchase_invoices'" @booked="loadItems" />

      <!-- Item History Dialog -->
      <v-dialog v-model="historyDialog" :max-width="$vuetify.breakpoint.xsOnly ? '' : '1100'" :fullscreen="$vuetify.breakpoint.xsOnly">
        <v-card>
          <v-toolbar dark color="primary">
            <v-toolbar-title class="white--text">
              {{ $t('inventory.item_history') }}: {{ historyDialogItem ? historyDialogItem.name : '' }}
            </v-toolbar-title>
            <v-spacer />
            <v-btn icon dark :aria-label="$t('close')" @click="historyDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-toolbar>
          <v-card-text class="pt-5">
            <v-data-table
              :headers="itemHistoryHeaders"
              :items="itemTransactions"
              :loading="loadingItemTransactions"
              :items-per-page="10"
            >
              <template v-slot:item.transactionType="{ item }">
                <v-chip :color="getTransactionTypeColor(item.transactionType)" small dark>
                  {{ $t('inventory.transaction_types.' + item.transactionType) }}
                </v-chip>
              </template>
              <template v-slot:item.quantity="{ item }">
                <span :class="item.quantity >= 0 ? 'success--text' : 'error--text'">
                  {{ item.quantity >= 0 ? '+' : '' }}{{ formatQuantity(item.quantity) }}
                </span>
              </template>
              <template v-slot:item.warehouseName="{ item }">{{ warehouseLabel(item) }}</template>
              <template v-slot:item.createdAt="{ item }">
                {{ formatDate(item.createdAt) }}
              </template>
              <template v-slot:item.actions="{ item }">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon small v-bind="attrs" v-on="on" :aria-label="$t('inventory.edit_transaction')" @click="openEditTransaction(item)">
                      <v-icon small>mdi-pencil</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t('inventory.edit_transaction') }}</span>
                </v-tooltip>
              </template>
            </v-data-table>
          </v-card-text>
          <v-divider />
          <v-card-actions>
            <v-spacer />
            <v-btn text :block="$vuetify.breakpoint.xsOnly" @click="historyDialog = false">{{ $t('close') }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Edit Transaction Dialog -->
      <v-dialog
        v-model="editTransactionDialog"
        :max-width="$vuetify.breakpoint.xsOnly ? '' : '600'"
        :fullscreen="$vuetify.breakpoint.xsOnly"
        persistent
      >
        <v-card v-if="editTransactionItem">
          <v-toolbar dark color="primary">
            <v-toolbar-title class="white--text">{{ $t('inventory.edit_transaction') }}</v-toolbar-title>
            <v-spacer />
            <v-btn icon dark :aria-label="$t('close')" @click="editTransactionDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-toolbar>
          <v-card-text class="pt-5 gradient-icons">
            <div class="subtitle-1 mb-4">{{ transactionContext(editTransactionItem) }}</div>
            <v-text-field
              v-model="editTransactionForm.description"
              :label="$t('inventory.description')"
              outlined
              dense
              class="mb-2"
            />
            <template v-if="!editTransactionItem.dispatchNoteId">
              <template v-if="editTransactionItem.item && editTransactionItem.item.unit === 'm'">
                <v-text-field
                  v-model.number="editTransactionForm.pieces"
                  :label="$t('inventory.pieces')"
                  :suffix="piecesUnit"
                  type="number"
                  min="1"
                  outlined
                  dense
                  class="mb-2"
                />
                <v-text-field
                  v-model.number="editTransactionForm.unitQuantity"
                  :label="$t('inventory.meters_per_piece')"
                  suffix="m"
                  type="number"
                  step="0.1"
                  min="0.1"
                  outlined
                  dense
                  class="mb-2"
                />
                <v-text-field
                  v-model.number="editTransactionForm.quantity"
                  :label="$t('inventory.amount')"
                  :suffix="unitLabel(editTransactionItem.item)"
                  :disabled="editQuantityAutoCalculated"
                  type="number"
                  step="0.001"
                  outlined
                  dense
                />
              </template>
              <v-text-field
                v-else
                v-model.number="editTransactionForm.quantity"
                :label="$t('inventory.amount')"
                :suffix="unitLabel(editTransactionItem.item)"
                type="number"
                step="0.001"
                outlined
                dense
              />
            </template>
            <div v-else class="text--secondary mb-2">
              <v-icon small left class="no-gradient">mdi-information-outline</v-icon>
              {{ $t('inventory.transfer_quantities_locked') }}
            </div>
            <numeric-input
              v-if="editTransactionIsIncoming"
              :key="editTransactionItem.id"
              v-model="editTransactionForm.unitCost"
              :label="$t('inventory.unit_cost_in_unit', { unit: unitLabel(editTransactionItem.item) })"
              :options="priceOptions"
              suffix="€"
              outlined
              dense
              class="mt-2"
            />
          </v-card-text>
          <v-divider />
          <v-card-actions :class="$vuetify.breakpoint.xsOnly && 'flex-column-reverse'">
            <v-spacer />
            <v-btn text :block="$vuetify.breakpoint.xsOnly" @click="editTransactionDialog = false">{{ $t('cancel') }}</v-btn>
            <v-btn
              color="primary"
              :block="$vuetify.breakpoint.xsOnly"
              :class="$vuetify.breakpoint.xsOnly && 'mb-2'"
              :loading="savingTransaction"
              @click="saveEditTransaction"
            >
              {{ $t('save') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Delete Transaction Dialog -->
      <confirm-dialog ref="deleteTransactionConfirm" />

      <warehouse-management-dialog ref="warehouseManagement" @changed="loadWarehouses" @default-changed="warehouseId = $event" />

      <!-- Snapshot Excel Download Dialog -->
      <v-dialog v-model="snapshotDialog" :max-width="$vuetify.breakpoint.xsOnly ? '' : '400'" :fullscreen="$vuetify.breakpoint.xsOnly">
        <v-card>
          <v-toolbar dark color="primary">
            <v-toolbar-title class="white--text">{{ $t('inventory.download_snapshot') }}</v-toolbar-title>
            <v-spacer />
            <v-btn icon dark :aria-label="$t('close')" @click="snapshotDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-toolbar>
          <v-card-text class="pt-5 gradient-icons">
            <v-select
              v-model="snapshotWarehouse"
              :items="snapshotWarehouseOptions"
              item-text="name"
              item-value="id"
              :label="$t('inventory.warehouse')"
              prepend-inner-icon="mdi-warehouse"
              outlined
              dense
            />
            <v-select
              v-model="snapshotAccountingClass"
              :items="snapshotAccountingClassOptions"
              :label="$t('item_types.accounting_class')"
              prepend-inner-icon="mdi-scale-balance"
              outlined
              dense
            />
            <v-menu v-model="snapshotDateMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  :value="formatIsoDateFi(snapshotDate)"
                  :label="$t('inventory.snapshot_date')"
                  prepend-inner-icon="mdi-calendar"
                  readonly
                  hide-details
                  v-bind="attrs"
                  v-on="on"
                  outlined
                  dense
                />
              </template>
              <v-date-picker v-model="snapshotDate" no-title locale="fi" :first-day-of-week="1" @input="snapshotDateMenu = false" />
            </v-menu>
          </v-card-text>
          <v-divider />
          <v-card-actions :class="$vuetify.breakpoint.xsOnly && 'flex-column-reverse'">
            <v-spacer />
            <v-btn text :block="$vuetify.breakpoint.xsOnly" @click="snapshotDialog = false">{{ $t('common.cancel') }}</v-btn>
            <v-btn
              color="primary"
              :block="$vuetify.breakpoint.xsOnly"
              :class="$vuetify.breakpoint.xsOnly && 'mb-2'"
              :loading="downloadingSnapshot"
              @click="downloadSnapshotExcel"
            >
              <v-icon left class="no-gradient">mdi-download</v-icon>
              {{ $t('common.download') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Tapahtumahistoria Exceliin: aikaväli (ALIT-551) -->
      <v-dialog v-model="historyExcelDialog" :max-width="$vuetify.breakpoint.xsOnly ? '' : '400'" :fullscreen="$vuetify.breakpoint.xsOnly">
        <v-card>
          <v-toolbar dark color="primary">
            <v-toolbar-title class="white--text">{{ $t('inventory.download_history_excel') }}</v-toolbar-title>
            <v-spacer />
            <v-btn icon dark :aria-label="$t('close')" @click="historyExcelDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-toolbar>
          <v-card-text class="pt-5 gradient-icons">
            <div class="mb-2">{{ $t('inventory.history_excel_period') }}</div>
            <v-text-field
              :value="formatIsoDateRangeFi(historyExcelDates)"
              readonly
              outlined
              dense
              hide-details
              prepend-inner-icon="mdi-calendar"
              class="mb-2"
            />
            <v-date-picker
              v-model="historyExcelDates"
              full-width
              class="elevation-0"
              locale="fi"
              no-title
              range
              show-week
              :first-day-of-week="1"
              :locale-first-day-of-year="4"
            />
          </v-card-text>
          <v-divider />
          <v-card-actions :class="$vuetify.breakpoint.xsOnly && 'flex-column-reverse'">
            <v-spacer />
            <v-btn text :block="$vuetify.breakpoint.xsOnly" @click="historyExcelDialog = false">{{ $t('common.cancel') }}</v-btn>
            <v-btn
              color="primary"
              :block="$vuetify.breakpoint.xsOnly"
              :class="$vuetify.breakpoint.xsOnly && 'mb-2'"
              :disabled="historyExcelDates.length !== 2"
              :loading="downloadingHistoryExcel"
              @click="downloadHistoryExcel"
            >
              <v-icon left class="no-gradient">mdi-download</v-icon>
              {{ $t('common.download') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Recalculate Balances Dialog -->
      <v-dialog v-model="recalculateDialog" :max-width="$vuetify.breakpoint.xsOnly ? '' : '500'" :fullscreen="$vuetify.breakpoint.xsOnly">
        <v-card>
          <v-toolbar dark color="primary">
            <v-toolbar-title class="white--text">{{ $t('inventory.recalculate_balances') }}</v-toolbar-title>
            <v-spacer />
            <v-btn icon dark :aria-label="$t('close')" @click="recalculateDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-toolbar>
          <v-card-text class="pt-5">{{ $t('inventory.recalculate_balances_description') }}</v-card-text>
          <v-divider />
          <v-card-actions :class="$vuetify.breakpoint.xsOnly && 'flex-column-reverse'">
            <v-spacer />
            <v-btn text :block="$vuetify.breakpoint.xsOnly" @click="recalculateDialog = false">{{ $t('common.cancel') }}</v-btn>
            <v-btn
              color="primary"
              :block="$vuetify.breakpoint.xsOnly"
              :class="$vuetify.breakpoint.xsOnly && 'mb-2'"
              :loading="recalculating"
              @click="doRecalculateBalances"
            >
              {{ $t('inventory.recalculate_balances_confirm') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Snackbars -->
      <v-snackbar v-model="successSnackbar" :timeout="2000" top color="success">
        {{ successMessage }}
      </v-snackbar>
      <v-snackbar v-model="errorSnackbar" :timeout="3000" top color="error">
        {{ errorMessage }}
      </v-snackbar>
    </v-container>
  </hb-basic-page>
</template>

<script>
import inventoryApi from '@/api/inventory';
import itemApi from '@/api/item';
import { ACCOUNTING_CLASS, isTrackable, loadItemTypes, typeIds } from '@/helpers/itemTypes';
import ConfirmDialog from '@/components/ConfirmDialog';
import InventoryPurchaseInvoices from '@/components/Inventory/InventoryPurchaseInvoices';
import InventoryTransactionForm from '@/components/Inventory/InventoryTransactionForm';
import WarehouseManagementDialog from '@/components/Inventory/WarehouseManagementDialog';
import ItemCombobox from '@/components/ItemCombobox';
import NumericInput from '@/components/NumericInput';
import NoContentView from '@/components/NoContentView';
import debounce from 'lodash.debounce';
import listQuery, { queryModels } from '@/helpers/listQuery';
import { PIECES_UNIT, formatEuro, formatIsoDateFi, formatQuantity, unitLabel } from '@/helpers/inventoryFormat';
import { defaultWarehouseId, rememberWarehouseId } from '@/helpers/selectedWarehouse';

// v-select ei valitse null-arvoista vaihtoehtoa
const ALL_WAREHOUSES = 'all';
const ALL_ACCOUNTING_CLASSES = 'all';

const TABS = ['balances', 'transactions', 'purchase_invoices'];
const TRANSACTION_TYPES = ['add', 'remove', 'adjust', 'transfer'];

// Välilehti, tapahtumatyyppi ja historian suodattimet pidetään URL:ssa (helpers/listQuery).
const QUERY_FIELDS = {
  tab: { type: 'string', default: 'balances' },
  type: { type: 'string', default: 'add' },
  q: { type: 'string', default: '' },
  item: { type: 'int', default: null, nullable: true },
  from: { type: 'string', default: '' },
  to: { type: 'string', default: '' },
  page: { type: 'int', default: 1 },
  size: { type: 'int', default: 50, allowed: [50, 100, 500, -1], remember: true },
  sort: { type: 'string', default: 'createdAt', remember: true },
  desc: { type: 'bool', default: true, remember: true },
};

// Vanhat linkit ?tab=add|remove|adjust|transfer|history ohjataan Tapahtumat-välilehdelle.
function resolveTab(query) {
  if (TABS.includes(query.tab)) return query.tab;
  if (TRANSACTION_TYPES.includes(query.tab) || query.tab === 'history') return 'transactions';
  return 'balances';
}

function resolveTransactionType(query) {
  // Vanha linkki ?tab=remove voittaa, koska listState täyttää type-kenttään oletuksen 'add'.
  if (TRANSACTION_TYPES.includes(query.tab)) return query.tab;
  if (TRANSACTION_TYPES.includes(query.type)) return query.type;
  return 'add';
}

export default {
  name: 'InventoryView',
  components: { ConfirmDialog, InventoryPurchaseInvoices, InventoryTransactionForm, ItemCombobox, WarehouseManagementDialog, NoContentView, NumericInput },
  mixins: [listQuery({ key: 'inventory', fields: QUERY_FIELDS })],
  data() {
    return {
      loading: true,
      warehouses: [],
      warehouseId: null,
      formPending: false,
      loadingBalances: false,
      loadingTransactions: false,
      loadingItemTransactions: false,

      tabs: [
        { value: 'balances', text: this.$t('inventory.balances'), icon: 'mdi-warehouse' },
        { value: 'transactions', text: this.$t('inventory.transactions'), icon: 'mdi-swap-vertical' },
        { value: 'purchase_invoices', text: this.$t('inventory.purchase_invoices.title'), icon: 'mdi-receipt' },
      ],

      items: [],
      balances: [],
      transactions: {},
      itemTransactions: [],

      searchBalance: '',
      piecesUnit: PIECES_UNIT,
      // Aikaväli kirjataan URL:iin vasta, kun molemmat päivät on valittu
      historyDates: [],
      historyDateMenu: false,

      priceOptions: {
        decimalCharacter: ',',
        digitGroupSeparator: ' ',
        decimalPlaces: 4,
        allowDecimalPadding: false,
        emptyInputBehavior: 'null',
      },

      // Dialogs
      historyDialog: false,
      historyDialogItem: null,

      // Edit transaction dialog
      editTransactionDialog: false,
      editTransactionItem: null,
      editTransactionForm: { description: '', quantity: null, pieces: null, unitQuantity: null, unitCost: null },
      savingTransaction: false,

      // Delete transaction
      deleteTransactionItem: null,

      // Snapshot Excel download
      snapshotDialog: false,
      snapshotWarehouse: null,
      // Vaihto-omaisuutta seurataan eniten (ALIT-627)
      snapshotAccountingClass: ACCOUNTING_CLASS.VAIHTOOMAISUUS,
      snapshotDate: new Date().toISOString().substr(0, 10),
      snapshotDateMenu: false,
      downloadingSnapshot: false,

      // Tapahtumahistoria Exceliin (ALIT-551): oletuksena kuluva kuukausi tähän päivään
      historyExcelDialog: false,
      historyExcelDates: [
        new Date().toISOString().substr(0, 8) + '01',
        new Date().toISOString().substr(0, 10),
      ],
      downloadingHistoryExcel: false,

      // Recalculate dialog
      recalculateDialog: false,
      recalculating: false,

      // Snackbars
      successSnackbar: false,
      successMessage: '',
      errorSnackbar: false,
      errorMessage: '',

      // Table headers
      balanceHeaders: [
        { text: this.$t('inventory.item_name'), value: 'item.name' },
        { text: this.$t('inventory.item_type'), value: 'item.typeName' },
        { text: this.$t('inventory.quantity'), value: 'quantity' },
        { text: this.$t('inventory.updated_at'), value: 'updatedAt' },
        { text: '', value: 'actions', sortable: false },
      ],
      transactionHeaders: [
        { text: this.$t('inventory.item_name'), value: 'item.name' },
        { text: this.$t('inventory.type'), value: 'transactionType' },
        { text: this.$t('inventory.amount'), value: 'quantity' },
        { text: this.$t('inventory.description'), value: 'description' },
        { text: this.$t('inventory.site'), value: 'siteName' },
        { text: this.$t('inventory.warehouse'), value: 'warehouseName' },
        { text: this.$t('inventory.user'), value: 'userName' },
        { text: this.$t('inventory.date'), value: 'createdAt' },
      ],
      // Palvelinsivutus: vain tietokannan sarakkeet ovat järjestettäviä
      transactionHeadersWithActions: [
        { text: this.$t('inventory.item_name'), value: 'item.name' },
        { text: this.$t('inventory.type'), value: 'transactionType' },
        { text: this.$t('inventory.amount'), value: 'quantity' },
        { text: this.$t('inventory.description'), value: 'description' },
        { text: this.$t('inventory.site'), value: 'siteName', sortable: false },
        { text: this.$t('inventory.warehouse'), value: 'warehouseName', sortable: false },
        { text: this.$t('inventory.user'), value: 'userName', sortable: false },
        { text: this.$t('inventory.date'), value: 'createdAt' },
        { text: '', value: 'actions', sortable: false, align: 'end', width: 96 },
      ],
      draftDispatchNotes: [],
      loadingDraftDispatchNotes: false,
      draftDispatchHeaders: [
        { text: this.$t('inventory.dispatch_number'), value: 'dispatchNumber' },
        { text: this.$t('inventory.site'), value: 'siteName' },
        { text: this.$t('inventory.warehouse'), value: 'warehouseName' },
        { text: this.$t('inventory.direction'), value: 'direction' },
        { text: this.$t('inventory.date'), value: 'createdAt' },
        { text: this.$t('inventory.created_by'), value: 'createdBy' },
      ],
    };
  },
  computed: {
    ...queryModels({ historyItem: 'item' }),
    selectedAction: {
      get() {
        return resolveTab(this.listState);
      },
      set(tab) {
        this.updateQuery({ tab });
      },
    },
    transactionType: {
      get() {
        return resolveTransactionType(this.listState);
      },
      set(type) {
        this.updateQuery({ type });
      },
    },
    selectedWarehouseName() {
      const warehouse = this.warehouses.find(w => w.id === this.warehouseId);
      return warehouse ? warehouse.name : '';
    },
    itemHistoryHeaders() {
      return [...this.transactionHeaders, { text: '', value: 'actions', sortable: false, align: 'end', width: 56 }];
    },
    hasWarehouses() {
      return this.warehouses.length > 0;
    },
    showWarehouseSelect() {
      return this.hasWarehouses && ['balances', 'transactions'].includes(this.selectedAction);
    },
    // Lomakkeen rivien nykysaldot on haettu valitusta varastosta, joten varastoa ei vaihdeta kesken.
    hasPendingRows() {
      return this.formPending;
    },
    snapshotWarehouseOptions() {
      return [{ id: ALL_WAREHOUSES, name: this.$t('inventory.all_warehouses') }, ...this.warehouses];
    },
    snapshotAccountingClassOptions() {
      return [
        { value: ACCOUNTING_CLASS.VAIHTOOMAISUUS, text: this.$t('item_types.accounting_classes.vaihtoomaisuus') },
        { value: ACCOUNTING_CLASS.KAYTTOOMAISUUS, text: this.$t('item_types.accounting_classes.kayttoomaisuus') },
        { value: ALL_ACCOUNTING_CLASSES, text: this.$t('inventory.snapshot_all_accounting_classes') },
      ];
    },
    editTransactionIsIncoming() {
      const t = this.editTransactionItem;
      return !!t && (t.transactionType === 'IN' || (t.transactionType === 'ADJUSTMENT' && Number(t.quantity) > 0));
    },
    editQuantityAutoCalculated() {
      if (!this.editTransactionItem || !this.editTransactionItem.item) return false;
      if (this.editTransactionItem.item.unit !== 'm') return false;
      const p = this.editTransactionForm.pieces;
      return p !== null && p !== undefined && p !== '' && Number(p) > 0;
    },
    trackableItems() {
      return this.items.filter(item => isTrackable(item.typeId));
    },
    // Suodattimen tila on URL-queryssä id:nä; ItemCombobox käyttää nimikeobjektia.
    historyItemObject() {
      return this.trackableItems.find(item => item.id === this.historyItem) || null;
    },
    filteredBalances() {
      if (!this.searchBalance) {
        return this.balances;
      }
      const search = this.searchBalance.toLowerCase();
      return this.balances.filter(b =>
        b.item.name.toLowerCase().includes(search) ||
        (b.item.code && b.item.code.toLowerCase().includes(search)) ||
        (b.item.typeName && b.item.typeName.toLowerCase().includes(search))
      );
    },
  },
  methods: {
    async loadItems() {
      try {
        const [items] = await Promise.all([itemApi.getItems(), loadItemTypes()]);
        this.items = items;
      } catch (error) {
        console.error('Error loading items:', error);
        this.showError(this.$t('inventory.errors.load_items'));
      }
    },

    async loadWarehouses() {
      try {
        this.warehouses = await inventoryApi.getWarehouses();
        if (!this.warehouses.some(w => w.id === this.warehouseId)) {
          this.warehouseId = defaultWarehouseId(this.warehouses) || (this.warehouses[0] && this.warehouses[0].id);
        }
      } catch (error) {
        console.error('Error loading warehouses:', error);
        this.showError(this.$t('inventory.errors.load_warehouses'));
      }
    },

    async loadBalances() {
      if (!this.warehouseId) return;
      this.loadingBalances = true;
      try {
        this.balances = await inventoryApi.getWarehouseBalances(this.warehouseId);
      } catch (error) {
        console.error('Error loading balances:', error);
        this.showError(this.$t('inventory.errors.load_balances'));
      } finally {
        this.loadingBalances = false;
      }
    },

    async loadDraftDispatchNotes() {
      this.loadingDraftDispatchNotes = true;
      try {
        const all = await inventoryApi.getAllDispatchNotes();
        this.draftDispatchNotes = all.filter(d => d.status === 'DRAFT');
      } catch (error) {
        console.error('Error loading draft dispatch notes:', error);
      } finally {
        this.loadingDraftDispatchNotes = false;
      }
    },

    async loadTransactionHistory() {
      this.loadingTransactions = true;
      const s = this.listState;
      const filter = { warehouseId: this.warehouseId };
      if (s.q.trim()) filter.description = s.q.trim();
      if (s.item) filter.itemId = s.item;
      if (s.from) filter.from = s.from;
      if (s.to) filter.to = s.to;
      try {
        this.transactions = await inventoryApi.getWarehouseTransactionHistoryPaged(filter, this.tableOptions);
      } catch (error) {
        console.error('Error loading transactions:', error);
        this.showError(this.$t('inventory.errors.load_transactions'));
      } finally {
        this.loadingTransactions = false;
      }
    },

    // Varastot ja nimikkeet ladataan kerran; ensimmäinen reload() tulee listQuerystä ennen created-hookia.
    ensureReady() {
      if (!this.ready) {
        this.ready = Promise.all([this.loadWarehouses(), this.loadItems()]).then(() => {
          this.loading = false;
        });
      }
      return this.ready;
    },

    // listQuery kutsuu aina, kun URL:n query muuttuu (välilehti, tyyppi, suodattimet, sivu, järjestys).
    async reload() {
      const s = this.listState;
      const tab = resolveTab(s);
      const type = resolveTransactionType(s);
      // Vanha linkki (?tab=remove, ?tab=history) normalisoidaan; uusi query käynnistää latauksen uudestaan.
      if (s.tab !== tab || s.type !== type) {
        this.updateQuery({ tab, type });
        return;
      }
      await this.ensureReady();
      if (tab === 'balances') {
        await this.loadBalances();
      } else if (tab === 'transactions') {
        await Promise.all([this.loadTransactionHistory(), this.loadDraftDispatchNotes()]);
      }
    },

    onTransactionSaved() {
      this.loadBalances();
      this.loadTransactionHistory();
      // Varastoon lisäys päivittää nimikkeen hinnan, jonka lomake esitäyttää
      if (this.transactionType === 'add') this.loadItems();
    },

    onHistoryDates(dates) {
      if (dates.length !== 2) return;
      const [from, to] = [...dates].sort();
      this.historyDateMenu = false;
      this.updateQuery({ from, to });
    },

    openDispatchNoteSite(dispatchNote) {
      if (!dispatchNote.siteId) return;
      this.$router.push({ name: 'tenant_site_edit_site', params: { id: dispatchNote.siteId, tab: 'materials' } });
    },

    signedQuantity(transaction) {
      const quantity = Number(transaction.quantity);
      return `${quantity >= 0 ? '+' : ''}${formatQuantity(quantity)} ${unitLabel(transaction.item)}`;
    },

    // Esim. "Larssen 603 · Korjaus +3 m · PORI · 7.10.2026"
    transactionContext(transaction) {
      return [
        transaction.item && transaction.item.name,
        `${this.$t('inventory.transaction_types.' + transaction.transactionType)} ${this.signedQuantity(transaction)}`,
        this.warehouseLabel(transaction),
        this.formatDate(transaction.createdAt),
      ]
        .filter(Boolean)
        .join(' · ');
    },

    openEditTransaction(transaction) {
      this.editTransactionItem = transaction;
      this.editTransactionForm = {
        description: transaction.description || '',
        quantity: transaction.quantity,
        pieces: transaction.pieces,
        unitQuantity: transaction.unitQuantity,
        unitCost: transaction.unitCost,
      };
      this.editTransactionDialog = true;
    },

    async saveEditTransaction() {
      this.savingTransaction = true;
      try {
        const payload = { description: this.editTransactionForm.description };
        if (!this.editTransactionItem.dispatchNoteId) {
          if (this.editTransactionForm.quantity !== null && this.editTransactionForm.quantity !== '') {
            payload.quantity = this.editTransactionForm.quantity;
          }
          if (this.editTransactionForm.pieces !== null && this.editTransactionForm.pieces !== '') {
            payload.pieces = this.editTransactionForm.pieces;
          }
          if (this.editTransactionForm.unitQuantity !== null && this.editTransactionForm.unitQuantity !== '') {
            payload.unitQuantity = this.editTransactionForm.unitQuantity;
          }
        }
        const unitCost = this.editTransactionForm.unitCost;
        if (this.editTransactionIsIncoming && unitCost !== null && Number(unitCost) !== Number(this.editTransactionItem.unitCost)) {
          payload.unitCost = unitCost;
        }
        await inventoryApi.updateTransaction(this.editTransactionItem.id, payload);
        this.showSuccess(this.$t('inventory.success.transaction_updated'));
        this.editTransactionDialog = false;
        // Reload history and balances
        await Promise.all([
          this.loadTransactionHistory(),
          this.loadBalances(),
        ]);
        if (this.historyDialog && this.historyDialogItem) {
          await this.showItemHistory(this.historyDialogItem.id);
        }
      } catch (error) {
        console.error('Error updating transaction:', error);
        this.showError(this.$t('inventory.errors.transaction_update_failed'));
      } finally {
        this.savingTransaction = false;
      }
    },

    openDeleteTransaction(item) {
      this.deleteTransactionItem = item;
      this.$refs.deleteTransactionConfirm
        .open(
          this.$t('inventory.delete_transaction_named', { item: item.item ? item.item.name : '' }),
          this.$t('inventory.confirm_delete_transaction_details', {
            type: this.$t('inventory.transaction_types.' + item.transactionType),
            quantity: this.signedQuantity(item),
            warehouse: this.warehouseLabel(item) || '',
            date: this.formatDate(item.createdAt),
          }),
          { color: 'error', agree: this.$t('inventory.delete_transaction'), width: 460 }
        )
        .then(async confirmed => {
          if (!confirmed) return;
          try {
            await inventoryApi.deleteTransaction(this.deleteTransactionItem.id);
            this.showSuccess(this.$t('inventory.success.transaction_deleted'));
            await Promise.all([
              this.loadTransactionHistory(),
              this.loadBalances(),
            ]);
          } catch (e) {
            this.showError(this.$t('inventory.errors.transaction_delete_failed'));
          }
        });
    },

    async showItemHistory(itemId) {
      this.historyDialogItem = this.items.find(i => i.id === itemId);
      this.historyDialog = true;
      this.loadingItemTransactions = true;
      try {
        this.itemTransactions = await inventoryApi.getItemTransactionHistory(itemId, undefined, this.warehouseId);
      } catch (error) {
        console.error('Error loading item transactions:', error);
      } finally {
        this.loadingItemTransactions = false;
      }
    },

    formatEuro,
    unitLabel,

    openSnapshotDialog() {
      this.snapshotWarehouse = this.warehouseId;
      this.snapshotDialog = true;
    },

    warehouseLabel(transaction) {
      if (transaction.targetWarehouseName) {
        return `${transaction.warehouseName} → ${transaction.targetWarehouseName}`;
      }
      return transaction.warehouseName;
    },

    formatIsoDateFi,

    formatIsoDateRangeFi(dates) {
      const sorted = [...(dates || [])].filter(Boolean).sort();
      return sorted.map(d => this.formatIsoDateFi(d)).join(' - ');
    },

    async downloadHistoryExcel() {
      // v-date-picker range antaa päivät valintajärjestyksessä, joten järjestetään.
      const [from, to] = [...this.historyExcelDates].sort();
      this.downloadingHistoryExcel = true;
      try {
        const blob = await inventoryApi.getWarehouseTransactionHistoryExcel(from, to, this.warehouseId);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `varastotapahtumat_${from}_${to}.xlsx`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        this.historyExcelDialog = false;
      } catch (error) {
        console.error('Error downloading transaction history excel:', error);
        this.showError(this.$t('inventory.history_excel_failed'));
      } finally {
        this.downloadingHistoryExcel = false;
      }
    },

    formatQuantity,

    // Sama muoto kuin saldotaulussa (pp.kk.vvvv)
    formatDate(dateTime) {
      return this.$formatDateNoTime(dateTime);
    },

    getTransactionTypeColor(type) {
      const colors = {
        IN: 'success',
        OUT: 'warning',
        ADJUSTMENT: 'info',
        TRANSFER: 'secondary',
      };
      return colors[type] || 'grey';
    },

    showSuccess(message) {
      this.successMessage = message;
      this.successSnackbar = true;
    },

    showError(message) {
      this.errorMessage = message;
      this.errorSnackbar = true;
    },

    async doRecalculateBalances() {
      this.recalculating = true;
      try {
        await inventoryApi.recalculateBalances();
        this.recalculateDialog = false;
        this.showSuccess(this.$t('inventory.success.recalculated'));
        await this.loadBalances();
      } catch (error) {
        console.error('Error recalculating balances:', error);
        this.showError(this.$t('inventory.errors.recalculate_failed'));
      } finally {
        this.recalculating = false;
      }
    },

    async downloadSnapshotExcel() {
      this.downloadingSnapshot = true;
      try {
        const date = this.snapshotDate;
        const warehouseId = this.snapshotWarehouse === ALL_WAREHOUSES ? undefined : this.snapshotWarehouse;
        const accountingClass = this.snapshotAccountingClass === ALL_ACCOUNTING_CLASSES ? undefined : this.snapshotAccountingClass;
        const { blob, filename } = await inventoryApi.getWarehouseSnapshot(
          date,
          typeIds(type => type.trackInventory),
          warehouseId,
          accountingClass
        );
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        this.snapshotDialog = false;
      } catch (error) {
        console.error('Error downloading snapshot:', error);
        this.showError(this.$t('inventory.errors.download_snapshot_failed'));
      } finally {
        this.downloadingSnapshot = false;
      }
    },
  },
  watch: {
    'editTransactionForm.pieces'(newVal) {
      if (newVal !== null && newVal !== undefined && newVal !== '' && Number(newVal) > 0) {
        const uq = this.editTransactionForm.unitQuantity;
        const abs = uq ? Number(newVal) * Number(uq) : Number(newVal);
        const sign = this.editTransactionItem && Number(this.editTransactionItem.quantity) < 0 ? -1 : 1;
        this.editTransactionForm.quantity = sign * abs;
      }
    },
    'editTransactionForm.unitQuantity'(newVal) {
      const pieces = this.editTransactionForm.pieces;
      if (pieces !== null && pieces !== undefined && pieces !== '' && Number(pieces) > 0) {
        const abs = newVal ? Number(pieces) * Number(newVal) : Number(pieces);
        const sign = this.editTransactionItem && Number(this.editTransactionItem.quantity) < 0 ? -1 : 1;
        this.editTransactionForm.quantity = sign * abs;
      }
    },
    warehouseId(newVal, oldVal) {
      if (!newVal) return;
      rememberWarehouseId(newVal);
      // Ensimmäinen valinta tehdään latauksessa, joka hakee saldot ja historian itse.
      if (!oldVal) return;
      this.loadBalances();
      if (this.selectedAction === 'transactions') this.loadTransactionHistory();
    },
    listState: {
      immediate: true,
      handler(s) {
        this.historyDates = [s.from, s.to].filter(Boolean);
      },
    },
  },
  created() {
    this.debounceSearch = debounce(q => this.updateQuery({ q: q || '' }), 400, { maxWait: 1000 });
  },
};
</script>

<style scoped>
.inventory-view {
  min-height: 100vh;
}
</style>
